import { Data, Effect } from 'effect';
import { doc } from './url';

type BrowserRuntimeGlobal = typeof globalThis & {
  OkraRuntime?: {
    doc: typeof doc;
  };
};

// ── Redacted failure channel ─────────────────────────────────────────────────

export type BrowserRuntimeOperation = 'install';

/**
 * Safe browser-runtime failure: the payload carries an operation label only —
 * never the global object's contents or the host's exception message. The
 * original cause is kept in a module-level WeakMap and restored by
 * `browserRuntimeCause` / `restoreBrowserRuntimeError`, so a host that throws
 * on the global assignment (frozen realm, hostile getter/setter) rejects with
 * the exact value the plain assignment would have thrown.
 */
export class BrowserRuntimeError extends Data.TaggedError('BrowserRuntimeError')<{
  readonly operation: BrowserRuntimeOperation;
  readonly message: string;
}> {}

const browserRuntimeCauses = new WeakMap<BrowserRuntimeError, unknown>();

function browserRuntimeFailure(operation: BrowserRuntimeOperation, cause: unknown) {
  const error = new BrowserRuntimeError({
    operation,
    message: 'Unable to install the okra browser runtime.',
  });
  browserRuntimeCauses.set(error, cause);
  return error;
}

export function browserRuntimeCause(error: BrowserRuntimeError): unknown {
  return browserRuntimeCauses.get(error) ?? error;
}

/** Restore the original cause so callers observe the legacy thrown value. */
export function restoreBrowserRuntimeError<A>(
  program: Effect.Effect<A, BrowserRuntimeError>,
): Effect.Effect<A, unknown> {
  return program.pipe(
    Effect.catch((error) => Effect.fail(browserRuntimeCauses.get(error) ?? error)),
  );
}

/**
 * Install the `doc` URL builder onto `OkraRuntime` on the given global. This
 * module's entire purpose is that import-time side effect, so the assignment
 * itself is the native program; the `*Effect` export is what the module runs.
 */
export const installOkraBrowserRuntimeEffect = Effect.fn('browser.installRuntime')(function* (
  target: BrowserRuntimeGlobal,
): Effect.fn.Return<void, BrowserRuntimeError> {
  yield* Effect.try({
    try: () => {
      target.OkraRuntime = {
        ...(target.OkraRuntime || {}),
        doc,
      };
    },
    catch: (cause) => browserRuntimeFailure('install', cause),
  });
});

const runtimeGlobal = globalThis as BrowserRuntimeGlobal;
Effect.runSync(restoreBrowserRuntimeError(installOkraBrowserRuntimeEffect(runtimeGlobal)));

export { doc };
export default { doc };
